import ArchiveIcon from '@mui/icons-material/Archive';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  type OnboardingSubmissionDetail,
  useGetOnboardingSubmissionQuery,
  useLinkSubmissionMutation,
  useUnlinkSubmissionMutation,
  useUpdateSubmissionStatusMutation,
} from './api/onboardingApi';

// ---------------------------------------------------------------------------
// Generic answer renderer
// ---------------------------------------------------------------------------

interface DefinitionField {
  id?: string;
  ref?: string;
  title?: string;
  type?: string;
  properties?: Record<string, unknown>;
}

interface AnswerRecord {
  field?: { id?: string; ref?: string; type?: string };
  type?: string;
  text?: string;
  number?: number;
  boolean?: boolean;
  email?: string;
  date?: string;
  url?: string;
  file_url?: string;
  phone_number?: string;
  choice?: { id?: string; label?: string; ref?: string; other?: string };
  choices?: { ids?: string[]; labels?: string[]; refs?: string[]; other?: string };
  payment?: { amount?: string; last4?: string; name?: string };
}

/** Returns the URL only if it starts with http(s), otherwise '#'. */
function safeUrl(url: string | undefined): string {
  if (!url) return '#';
  return /^https?:\/\//i.test(url) ? url : '#';
}

function formatAnswerValue(answer: AnswerRecord): React.ReactNode {
  switch (answer.type) {
    case 'text':
      return (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {answer.text}
        </Typography>
      );
    case 'number':
      return <Typography variant="body2">{answer.number}</Typography>;
    case 'boolean':
      return (
        <Chip
          label={answer.boolean ? 'Yes' : 'No'}
          color={answer.boolean ? 'success' : 'default'}
          size="small"
        />
      );
    case 'email':
      return (
        <Typography variant="body2">
          <a href={`mailto:${answer.email}`}>{answer.email}</a>
        </Typography>
      );
    case 'date':
      return (
        <Typography variant="body2">
          {answer.date ? new Date(answer.date).toLocaleDateString() : '—'}
        </Typography>
      );
    case 'url':
      return (
        <Typography variant="body2">
          <a href={safeUrl(answer.url)} target="_blank" rel="noopener noreferrer">
            {answer.url}
          </a>
        </Typography>
      );
    case 'file_url':
      return (
        <Typography variant="body2">
          <a href={safeUrl(answer.file_url)} target="_blank" rel="noopener noreferrer">
            Download file
          </a>
        </Typography>
      );
    case 'phone_number':
      return <Typography variant="body2">{answer.phone_number}</Typography>;
    case 'choice':
      return (
        <Typography variant="body2">
          {answer.choice?.other ?? answer.choice?.label ?? '—'}
        </Typography>
      );
    case 'choices': {
      const labels = answer.choices?.labels ?? [];
      const other = answer.choices?.other;
      const all = other ? [...labels, other] : labels;
      return <Typography variant="body2">{all.join(', ') || '—'}</Typography>;
    }
    case 'payment':
      return (
        <Typography variant="body2">
          {answer.payment?.amount ?? '—'} (card ending {answer.payment?.last4 ?? '????'})
        </Typography>
      );
    default:
      return (
        <Typography variant="body2" color="text.secondary">
          <code>{JSON.stringify(answer, null, 2)}</code>
        </Typography>
      );
  }
}

function AnswersSection({ submission }: { submission: OnboardingSubmissionDetail }) {
  const definition = submission.definition as { fields?: DefinitionField[] } | undefined;
  const fields = definition?.fields ?? [];
  const answers = (submission.answers ?? []) as AnswerRecord[];

  // Build a map from field ID to answer for quick lookup
  const answerMap = new Map<string, AnswerRecord>();
  for (const answer of answers) {
    const fieldId = answer.field?.id;
    if (fieldId) answerMap.set(fieldId, answer);
  }

  if (fields.length === 0 && answers.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No answers recorded
      </Typography>
    );
  }

  // If we have definition fields, render in order; otherwise fall back to raw answers
  const items =
    fields.length > 0
      ? fields.map((field) => ({
          title: field.title ?? field.ref ?? field.id ?? 'Unknown field',
          fieldType: field.type ?? 'unknown',
          answer: field.id ? answerMap.get(field.id) : undefined,
        }))
      : answers.map((answer, idx) => ({
          title: answer.field?.ref ?? answer.field?.id ?? `Answer ${idx + 1}`,
          fieldType: answer.type ?? 'unknown',
          answer,
        }));

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {items.map((item, idx) => (
        <Paper key={item.title + String(idx)} variant="outlined" sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            {item.fieldType}
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            {item.title}
          </Typography>
          {item.answer ? (
            formatAnswerValue(item.answer)
          ) : (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              No answer provided
            </Typography>
          )}
        </Paper>
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// JSON viewer for raw payload sections
// ---------------------------------------------------------------------------

function JsonSection({ title, data }: { title: string; data: unknown }) {
  if (data === null || data === undefined) return null;

  // Skip empty objects/arrays
  if (typeof data === 'object' && Object.keys(data as object).length === 0) return null;

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          component="pre"
          sx={{
            p: 1,
            borderRadius: 1,
            bgcolor: 'grey.50',
            overflow: 'auto',
            maxHeight: 400,
            fontSize: '0.8rem',
            fontFamily: 'monospace',
          }}
        >
          {JSON.stringify(data, null, 2)}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function TypeformSubmissionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useGetOnboardingSubmissionQuery(
    { id: id ?? '' },
    { skip: !id }
  );
  const [linkSubmission] = useLinkSubmissionMutation();
  const [unlinkSubmission] = useUnlinkSubmissionMutation();
  const [updateStatus] = useUpdateSubmissionStatusMutation();

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [personIdInput, setPersonIdInput] = useState('');
  const [actionResult, setActionResult] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const submission = data?.submission;

  const handleLink = useCallback(async () => {
    if (!id || !personIdInput.trim()) return;
    try {
      await linkSubmission({ id, person_id: personIdInput.trim() }).unwrap();
      setActionResult({ type: 'success', message: 'Submission linked to person' });
      setLinkDialogOpen(false);
      setPersonIdInput('');
    } catch {
      setActionResult({ type: 'error', message: 'Failed to link submission' });
    }
  }, [id, personIdInput, linkSubmission]);

  const handleUnlink = useCallback(async () => {
    if (!id) return;
    try {
      await unlinkSubmission({ id }).unwrap();
      setActionResult({ type: 'success', message: 'Submission unlinked' });
    } catch {
      setActionResult({ type: 'error', message: 'Failed to unlink submission' });
    }
  }, [id, unlinkSubmission]);

  const handleStatusToggle = useCallback(async () => {
    if (!id || !submission) return;
    const newStatus = submission.status === 'active' ? 'archived' : 'active';
    try {
      await updateStatus({ id, status: newStatus }).unwrap();
      setActionResult({
        type: 'success',
        message: `Submission ${newStatus === 'archived' ? 'archived' : 'restored'}`,
      });
    } catch {
      setActionResult({ type: 'error', message: 'Failed to update status' });
    }
  }, [id, submission, updateStatus]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !submission) {
    return <Alert severity="error">Failed to load submission</Alert>;
  }

  const respondentName =
    [submission.respondent_first_name, submission.respondent_last_name].filter(Boolean).join(' ') ||
    'Unknown';
  const isUnrecognized = submission.form_type.startsWith('unrecognized:');

  return (
    <Box>
      {/* Back button + header */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <IconButton onClick={() => navigate('/admin/typeform')}>
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h5">{respondentName}</Typography>
          <Box display="flex" gap={1} alignItems="center" mt={0.5}>
            {submission.respondent_email && (
              <Typography variant="body2" color="text.secondary">
                {submission.respondent_email}
              </Typography>
            )}
            {submission.respondent_company && (
              <>
                <Typography variant="body2" color="text.secondary">
                  &middot;
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {submission.respondent_company}
                </Typography>
              </>
            )}
          </Box>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          {isUnrecognized ? (
            <Chip label={submission.form_type} color="error" size="small" />
          ) : (
            <Chip label={submission.form_type} size="small" />
          )}
          <Chip
            label={submission.status}
            color={submission.status === 'active' ? 'success' : 'default'}
            size="small"
            variant="outlined"
          />
        </Box>
      </Box>

      {actionResult && (
        <Alert severity={actionResult.type} onClose={() => setActionResult(null)} sx={{ mb: 2 }}>
          {actionResult.message}
        </Alert>
      )}

      {/* Metadata row */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={4} flexWrap="wrap">
          <Box>
            <Typography variant="caption" color="text.secondary">
              Submitted
            </Typography>
            <Typography variant="body2">
              {new Date(submission.submitted_at).toLocaleString()}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Form ID
            </Typography>
            <Typography variant="body2" fontFamily="monospace">
              {submission.typeform_form_id}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Event ID
            </Typography>
            <Typography variant="body2" fontFamily="monospace">
              {submission.typeform_event_id}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Linked Person
            </Typography>
            <Typography variant="body2" fontFamily="monospace">
              {submission.linked_person_id ?? 'Not linked'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Actions */}
      <Box display="flex" gap={1} mb={2}>
        {submission.linked_person_id ? (
          <Button variant="outlined" startIcon={<LinkOffIcon />} onClick={handleUnlink}>
            Unlink Person
          </Button>
        ) : (
          <Button
            variant="outlined"
            startIcon={<LinkIcon />}
            onClick={() => setLinkDialogOpen(true)}
          >
            Link to Person
          </Button>
        )}
        <Button
          variant="outlined"
          color={submission.status === 'active' ? 'warning' : 'success'}
          startIcon={submission.status === 'active' ? <ArchiveIcon /> : <RestoreIcon />}
          onClick={handleStatusToggle}
        >
          {submission.status === 'active' ? 'Archive' : 'Restore'}
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Answers */}
      <Typography variant="h6" gutterBottom>
        Answers
      </Typography>
      <Box mb={3}>
        <AnswersSection submission={submission} />
      </Box>

      {/* Raw JSONB sections */}
      <Typography variant="h6" gutterBottom>
        Raw Data
      </Typography>
      <Box display="flex" flexDirection="column" gap={1}>
        <JsonSection title="Hidden Fields" data={submission.hidden} />
        <JsonSection title="Calculated Fields" data={submission.calculated} />
        <JsonSection title="Variables" data={submission.variables} />
        <JsonSection title="Definition" data={submission.definition} />
      </Box>

      {/* Link dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)}>
        <DialogTitle>Link to Person</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter the person ID (UUID) to link this submission to.
          </Typography>
          <TextField
            fullWidth
            label="Person ID"
            placeholder="e.g. 12345678-abcd-1234-abcd-1234567890ab"
            value={personIdInput}
            onChange={(e) => setPersonIdInput(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleLink} variant="contained" disabled={!personIdInput.trim()}>
            Link
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
